import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '../../../../ui/index.js';
import { formatMoney } from '../../format.js';
import { errorMessage } from '../../../../api/errors.js';
import { INSURANCE_PERCENT_DEFAULT } from '../../../../api/requests/index.js';
import {
  usePatchCarrierInsuranceQuote,
  useReconfirmCarrierInsuranceQuote,
  useDeleteCarrierInsuranceQuote,
} from '../../queries.js';
import type { CarrierInsuranceQuote } from '../../types.js';

export interface CarrierInsuranceQuoteRowProps {
  requestId: string;
  requestCarrierId: string;
  quote: CarrierInsuranceQuote;
  scopeLabel: string;
  canManage: boolean;
  canDelete: boolean;
  deleteDisabledReason?: string | null;
}

/**
 * @aiPurpose render one `RequestCarrierInsuranceQuote` row inside the
 *   carrier card's insurance section (#1163). Scope is immutable — the
 *   row exposes percent / comment edit, reconfirm, and delete. The
 *   primary REQUEST row delete is gated by `canDelete` (false when any
 *   override exists for the same carrier).
 * @aiInvariant `quote.amount` / `quote.currency` are populated by the
 *   backend only for the REQUEST-scope primary; overrides leave both
 *   null. The "needs reconfirm" chip is server-authoritative.
 */
export function CarrierInsuranceQuoteRow({
  requestId,
  requestCarrierId,
  quote,
  scopeLabel,
  canManage,
  canDelete,
  deleteDisabledReason,
}: CarrierInsuranceQuoteRowProps) {
  const { t } = useTranslation(['requests', 'common']);
  const patch = usePatchCarrierInsuranceQuote(requestId);
  const reconfirm = useReconfirmCarrierInsuranceQuote(requestId);
  const remove = useDeleteCarrierInsuranceQuote(requestId);
  const [editing, setEditing] = useState(false);
  const [percentStr, setPercentStr] = useState(String(quote.percent));
  const [comment, setComment] = useState(quote.comment);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const needsReconfirm = quote.needsReconfirm;

  function openEditor() {
    setSubmitError(null);
    setPercentStr(String(quote.percent));
    setComment(quote.comment);
    setEditing(true);
  }

  function onSubmit() {
    setSubmitError(null);
    const raw = percentStr.trim();
    const percent = raw === '' ? INSURANCE_PERCENT_DEFAULT : Number(raw);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      setSubmitError(t('requests:carriers.insurance.errors.percentBounds'));
      return;
    }
    patch.mutate(
      {
        requestCarrierId,
        quoteId: quote.id,
        body: { percent, comment: comment.trim() },
      },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => setSubmitError(errorMessage(err)),
      },
    );
  }

  function onReconfirm() {
    setSubmitError(null);
    reconfirm.mutate(
      { requestCarrierId, quoteId: quote.id },
      { onError: (err) => setSubmitError(errorMessage(err)) },
    );
  }

  function onDelete() {
    setSubmitError(null);
    remove.mutate(
      { requestCarrierId, quoteId: quote.id },
      { onError: (err) => setSubmitError(errorMessage(err)) },
    );
  }

  if (editing && canManage) {
    return (
      <div
        data-testid={`carrier-insurance-row-${quote.id}`}
        style={{ display: 'grid', gap: 6, fontSize: 12, paddingBlock: 4 }}
      >
        <div style={{ color: 'var(--fg-3)', fontSize: 11 }}>{scopeLabel}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'inline-flex', gap: 6, color: 'var(--fg-2)' }}>
            <span>{t('requests:carriers.insurance.percentLabel')}</span>
            <span style={{ width: 90 }}>
              <Input type="number" value={percentStr} onChange={setPercentStr} />
            </span>
          </label>
          <label
            style={{ flex: 1, minWidth: 220, display: 'inline-flex', gap: 6, color: 'var(--fg-2)' }}
          >
            <span>{t('requests:carriers.insurance.commentLabel')}</span>
            <span style={{ flex: 1 }}>
              <Input
                value={comment}
                onChange={setComment}
                placeholder={t('requests:carriers.insurance.commentPlaceholder')}
                maxLength={2000}
              />
            </span>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button kind="primary" size="sm" disabled={patch.isPending} onClick={onSubmit}>
            {t('common:actions.save')}
          </Button>
          <Button
            kind="ghost"
            size="sm"
            disabled={patch.isPending}
            onClick={() => setEditing(false)}
          >
            {t('common:actions.cancel')}
          </Button>
        </div>
        {submitError !== null && (
          <div style={{ color: 'var(--status-error-fg)' }}>{submitError}</div>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={`carrier-insurance-row-${quote.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        fontSize: 12,
        paddingBlock: 4,
      }}
    >
      <span style={{ color: 'var(--fg-3)', fontSize: 11, minWidth: 110 }}>{scopeLabel}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
        {t('requests:carriers.insurance.percentValue', { percent: quote.percent })}
      </span>
      {quote.amount !== null && quote.currency !== null && (
        <span style={{ color: 'var(--fg-2)', fontVariantNumeric: 'tabular-nums' }}>
          {t('requests:carriers.insurance.amountLabel')}:{' '}
          {formatMoney(quote.amount, quote.currency)}
        </span>
      )}
      {needsReconfirm && (
        <span
          data-testid={`carrier-insurance-stale-chip-${quote.id}`}
          style={{
            fontSize: 11,
            padding: '2px 6px',
            borderRadius: 'var(--r-1)',
            background: 'var(--status-warn-bg)',
            color: 'var(--status-warn-fg)',
            border: '1px solid var(--status-warn-fg)',
          }}
        >
          {t('requests:carriers.insurance.needsReconfirm')}
        </span>
      )}
      {quote.comment && (
        <span
          style={{
            color: 'var(--fg-2)',
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-wrap',
            flexBasis: '100%',
          }}
        >
          {quote.comment}
        </span>
      )}
      {canManage && (
        <span
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          {needsReconfirm && (
            <Button
              kind="secondary"
              size="sm"
              icon="check"
              disabled={reconfirm.isPending}
              onClick={onReconfirm}
            >
              {t('requests:carriers.insurance.reconfirm')}
            </Button>
          )}
          <Button kind="ghost" size="sm" onClick={openEditor}>
            {t('common:actions.edit')}
          </Button>
          <span
            title={canDelete ? undefined : (deleteDisabledReason ?? undefined)}
            style={{ display: 'inline-flex' }}
          >
            <Button
              kind="ghost"
              size="sm"
              disabled={!canDelete || remove.isPending}
              onClick={onDelete}
            >
              {t('common:actions.delete')}
            </Button>
          </span>
        </span>
      )}
      {submitError !== null && (
        <span style={{ color: 'var(--status-error-fg)', flexBasis: '100%' }}>{submitError}</span>
      )}
    </div>
  );
}
